/**
 * `useWorkflowSteps(workflowId)` — TanStack Query read hook for the steps that
 * belong to a single workflow.
 *
 * Implements the AC-1399 / AC-1377 P2.2 ticket and the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`, sections 2.4
 * (query key conventions), 5.2 (read-hook signatures), and 8.2 (Phase 2
 * verification table).
 *
 * ## Why this exists
 *
 * Today, `WorkflowEditor.tsx` loads steps indirectly through
 * `useFlowStore.loadWorkflow`, which calls `apiService.getWorkflow(id)` AND
 * `apiService.getWorkflowSteps(id)` and writes both into store state. That
 * path has no caching, no request deduplication, and forces step-level
 * invalidation to flow through manual `setState` calls scattered across
 * components. This hook is the canonical read path for steps; the migration
 * that removes the legacy store flow lands in AC-1401 — see TD section 2.8.
 *
 * Like `useWorkflow`, this hook MUST coexist with `flowStore.loadWorkflow`
 * during the Phase 2 transition. Both paths can subscribe to the same steps
 * concurrently — RQ's per-key request dedup makes the duplicate fetch cheap.
 * AC-1401 deletes the legacy path.
 *
 * ## Behavior
 *
 * - Fetches `apiService.getWorkflowSteps(workflowId)` with cache key
 *   `queryKeys.workflowSteps(workflowId)` (the AC-1396 factory; resolves to
 *   `['workflow', Number(id), 'steps']`). The factory coerces `'1'` and `1`
 *   to the same array shape, so route-string IDs and store-numeric IDs share
 *   a single cache slot.
 *
 *   The key is hierarchical under `queryKeys.workflow(id)` so a single
 *   `invalidateQueries({ queryKey: queryKeys.workflow(id) })` cascades to
 *   both the workflow detail and its steps. This is RQ's prefix-matching
 *   contract; do not flatten without reworking caller invalidation.
 *
 * - `enabled` is auto-disabled for empty IDs (`undefined`, `null`, `''`).
 *   Same rationale as `useWorkflow` — without this guard, a `useQuery` whose
 *   key includes `Number(undefined)` (`NaN`) would cache under a useless key
 *   AND fire an HTTP request for an unknown ID. Today's call sites all guard
 *   with `if (!id) return` inside `useEffect`; this hook bakes that guard in.
 *
 * - Caller-provided `options` flow through to `useQuery`. Callers can override
 *   `staleTime`, `enabled`, `refetchOnMount`, etc. per-call without forking
 *   the hook. The caller's `enabled` is AND'ed with the internal id-truthy
 *   guard, so passing `{ enabled: false }` always wins.
 *
 * - Caller MUST NOT override `queryKey` or `queryFn` — those are owned by the
 *   hook. The `Options` type below excludes them.
 *
 * ## Argument forwarded to `apiService.getWorkflowSteps`
 *
 * The `workflowId` value is passed through to `apiService.getWorkflowSteps`
 * as-is once the truthy-id guard has cleared. `getWorkflowSteps`
 * URL-interpolates its argument (`/api/v1/orchestrations/${workflowId}/steps`),
 * so both numeric and string IDs serialize to the same URL. We deliberately
 * do NOT pre-stringify here so the call-site argument matches what callers
 * actually have in scope (route params are strings, store IDs are numbers —
 * both are valid inputs and the cache key factory normalizes them under the
 * hood). AC-1401's typing pass will widen `apiService.getWorkflowSteps` to
 * `string | number` directly.
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { WorkflowStep } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Caller-overridable subset of `UseQueryOptions`. We intentionally exclude
 * `queryKey` and `queryFn` because they are owned by this hook — letting
 * callers override them defeats the centralization the migration depends on.
 */
export type UseWorkflowStepsOptions = Omit<
  UseQueryOptions<
    WorkflowStep[],
    Error,
    WorkflowStep[],
    ReturnType<typeof queryKeys.workflowSteps>
  >,
  'queryKey' | 'queryFn'
>;

/**
 * Returns the standard `UseQueryResult<WorkflowStep[]>` from TanStack Query.
 *
 * @param workflowId Workflow ID. May be `string`, `number`, `undefined`, or
 *                   `null`. Falsy values disable the query (no fetch fires).
 * @param options    Optional `useQuery` options. `queryKey` and `queryFn`
 *                   are excluded from this type because they are owned by
 *                   the hook.
 *
 * @example
 *   const { data: steps, isLoading } = useWorkflowSteps(workflowId);
 *   if (isLoading) return <Spinner />;
 *   return <FlowCanvas steps={steps ?? []} />;
 *
 * @example
 *   // Defer the fetch until the workflow detail loads first.
 *   const detail = useWorkflow(id);
 *   const steps  = useWorkflowSteps(id, { enabled: detail.isSuccess });
 */
export function useWorkflowSteps(
  workflowId: string | number | undefined | null,
  options?: UseWorkflowStepsOptions,
): UseQueryResult<WorkflowStep[], Error> {
  // Treat `undefined`, `null`, and `''` as "no ID yet". Anything else is a
  // valid input — the queryKeys factory will coerce it to the canonical
  // numeric form. We pass the SAME canonical form to the API call below so
  // the cache key and the request URL agree.
  const hasId = workflowId !== undefined && workflowId !== null && workflowId !== '';

  // The caller's `enabled` (if provided) is AND'ed with our internal
  // truthy-id guard. Either being falsy keeps the query disabled.
  const callerEnabled = options?.enabled;
  const enabled = hasId && (callerEnabled === undefined ? true : callerEnabled);

  return useQuery<
    WorkflowStep[],
    Error,
    WorkflowStep[],
    ReturnType<typeof queryKeys.workflowSteps>
  >({
    // `as` cast: when `hasId` is false we still need to return SOME key
    // shape (RQ requires queryKey unconditionally), but `enabled: false`
    // guarantees the queryFn never runs and the placeholder key is never
    // populated. We use `0` as the placeholder ID so the cast is safe.
    queryKey: queryKeys.workflowSteps(hasId ? (workflowId as string | number) : 0),
    // Forward `workflowId` as-is. `apiService.getWorkflowSteps`
    // URL-interpolates so both numeric and string forms work; we use a
    // runtime cast because TS's current signature narrows to `string`, but
    // the underlying call accepts either. AC-1401's typing pass widens the
    // signature directly.
    queryFn: () => apiService.getWorkflowSteps(workflowId as string),
    ...options,
    enabled,
  });
}

export default useWorkflowSteps;
